import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Institutional: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const demoRef = useRef<HTMLDivElement>(null);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goToCustomSolutions = () => {
    navigate('/custom-solutions');
  };

  // Auto-cycle through steps every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  // Get the video file based on current language
  const getVideoSource = () => {
    const lang = i18n.language.split('-')[0]; // Get 'en' from 'en-GB', etc.
    const langMap: { [key: string]: string } = {
      'en': 'EN',
      'es': 'ES',
      'pt': 'PT'
    };
    return `/demo-${langMap[lang] || 'EN'}.mp4`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-brand-dark to-slate-900">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-primary/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Header */}
      <Header 
        onWatchDemoClick={() => scrollToSection(demoRef)}
        onBookSetupClick={goToCustomSolutions}
      />

      {/* Hero Section */}
      <main className="relative pt-12 pb-4 lg:pb-0">
        <section className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-24 pb-20">
          <div className="text-center max-w-5xl mx-auto relative z-10">
            {/* Glowing badge */}
            <div className="inline-flex items-center px-4 py-2 bg-brand-primary/20 border border-brand-secondary/50 rounded-full mb-8 backdrop-blur-sm">
              <div className="w-2 h-2 bg-brand-secondary rounded-full mr-2 animate-pulse"></div>
              <span className="text-brand-secondary text-sm font-medium">{t('institutional.hero.whyItMatters')}</span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-8 leading-[1.1]">
              <span className="text-white">{t('institutional.hero.headline')}</span>
            </h1>
            
            <p className="text-xl sm:text-2xl md:text-3xl text-white/80 mb-12 leading-relaxed font-light">
              {t('institutional.hero.subheadline')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                onClick={() => scrollToSection(demoRef)}
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border-2 border-brand-secondary px-10 py-5 rounded-2xl font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:shadow-brand-secondary/40 hover:scale-105"
              >
                <span className="relative z-10 flex items-center justify-center">
                  {t('institutional.hero.primaryCta')}
                  <svg className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </button>
              
              <button
                onClick={goToCustomSolutions}
                className="group relative bg-gradient-to-r from-brand-secondary to-brand-primary text-white px-10 py-5 rounded-2xl font-bold text-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-brand-secondary/60 hover:scale-105"
              >
                {t('institutional.hero.secondaryCta')}
              </button>
            </div>
          </div>
        </section>

        {/* 4-Step Promise with gradient background */}
        <section className="relative py-12 sm:py-12 overflow-visible">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-primary/10 to-transparent pointer-events-none"></div>
          
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-white mb-10 sm:mb-16">
              {t('institutional.steps.title')}
            </h2>
            
            {/* Step Progress Indicators */}
            <div className="flex justify-center items-center gap-3 mb-8">
              {[0, 1, 2, 3].map((idx) => (
                <div key={idx} className="flex items-center">
                  <button
                    onClick={() => setActiveStep(idx)}
                    className={`w-3 h-3 rounded-full transition-all duration-500 ${
                      activeStep === idx 
                        ? 'bg-brand-secondary w-12 shadow-lg shadow-brand-secondary/50' 
                        : 'bg-white/20 hover:bg-white/40'
                    }`}
                    aria-label={`Go to step ${idx + 1}`}
                  />
                  {idx < 3 && (
                    <div className={`w-8 h-0.5 transition-all duration-500 ${
                      activeStep > idx ? 'bg-brand-secondary' : 'bg-white/20'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {[
                { icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', text: t('institutional.steps.step1'), num: '01' },
                { icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4', text: t('institutional.steps.step2'), num: '02' },
                { icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01', text: t('institutional.steps.step3'), num: '03' },
                { icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', text: t('institutional.steps.step4'), num: '04' }
              ].map((step, idx) => {
                const isActive = activeStep === idx;
                const isPast = idx < activeStep;
                
                return (
                  <div 
                    key={idx} 
                    onClick={() => setActiveStep(idx)}
                    className={`group relative cursor-pointer backdrop-blur-lg rounded-2xl sm:rounded-3xl p-6 sm:p-8 border transition-all duration-700 ${
                      isActive 
                        ? 'bg-gradient-to-br from-brand-secondary/20 to-brand-primary/20 border-brand-secondary shadow-2xl shadow-brand-secondary/40 -translate-y-3 scale-105' 
                        : isPast
                        ? 'bg-gradient-to-br from-white/10 to-white/15 border-white/30 opacity-70'
                        : 'bg-gradient-to-br from-white/5 to-white/10 border-white/20 opacity-50'
                    } hover:border-brand-secondary/50 hover:shadow-2xl hover:shadow-brand-secondary/20 hover:-translate-y-2`}
                  >
                    {/* Animated pulse effect for active step */}
                    {isActive && (
                      <div className="absolute -inset-1 bg-gradient-to-r from-brand-secondary to-brand-primary rounded-2xl sm:rounded-3xl blur-xl opacity-50 animate-pulse"></div>
                    )}
                    
                    <div className={`absolute top-4 sm:top-6 right-4 sm:right-6 text-5xl sm:text-6xl font-bold transition-all duration-700 ${
                      isActive ? 'text-brand-secondary/30' : 'text-white/5'
                    }`}>{step.num}</div>
                    
                    <div className={`relative w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 transition-all duration-700 ${
                      isActive ? 'scale-110 shadow-lg shadow-brand-secondary/50' : 'group-hover:scale-110'
                    }`}>
                      <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={step.icon} />
                      </svg>
                    </div>
                    
                    <p className={`text-base sm:text-lg font-medium leading-relaxed transition-all duration-700 ${
                      isActive ? 'text-white' : 'text-white/90'
                    }`}>{step.text}</p>
                    
                    {/* Active step indicator */}
                    {isActive && (
                      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                        <div className="w-2 h-2 bg-brand-secondary rounded-full animate-bounce"></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Link to detailed How It Works page */}
            <div className="text-center mt-12">
              <Link
                to="/how-it-works"
                className="inline-flex items-center text-brand-secondary hover:text-brand-primary font-semibold text-lg transition-colors duration-200 group"
              >
                {t('institutional.steps.learnMore')}
                <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* Demo Video Section with dramatic styling */}
        <section ref={demoRef} className="relative py-12 scroll-mt-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {t('institutional.demo.title')}
              </h2>
            </div>
            
            {/* Video Container with glow effect */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-3xl blur-xl opacity-75 group-hover:opacity-100 transition duration-1000"></div>
              
              <div className="relative bg-slate-900 rounded-2xl overflow-hidden shadow-2xl aspect-video border border-white/10">
                <video
                  key={i18n.language}
                  className="w-full h-full object-cover"
                  controls
                  crossOrigin="anonymous"
                  aria-label={t('institutional.demo.title')}
                >
                  <source src={getVideoSource()} type="video/mp4" />
                  <p className="text-white p-8">
                    Your browser does not support the video tag.
                  </p>
                </video>
              </div>
            </div>

            <p className="text-center text-white/70 mt-8 text-xl">
              {t('institutional.demo.subtitle')}
            </p>
          </div>
        </section>

        {/* CTA Section - Get Started */}
        <section className="relative py-24">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-secondary/5 to-transparent"></div>
          
          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition duration-1000"></div>
              
              <div className="relative bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-10 border border-brand-secondary/30 text-center">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  {t('institutional.cta.title')}
                </h2>
                <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
                  {t('institutional.cta.description')}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/solutions"
                    className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border-2 border-brand-secondary px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-xl hover:shadow-brand-secondary/40 hover:scale-105"
                  >
                    {t('institutional.cta.browseSolutions')}
                  </Link>
                  
                  <button
                    onClick={goToCustomSolutions}
                    className="group relative bg-gradient-to-r from-brand-secondary to-brand-primary text-white px-8 py-4 rounded-xl font-bold text-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-brand-secondary/60 hover:scale-105"
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      {t('institutional.cta.scheduleConsultation')}
                      <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-brand-primary to-brand-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Institutional;
